// SearchPage.jsx - Modificaciones
import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  EyeIcon,
  FunnelIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';

const SearchPage = () => {
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOption, setSortOption] = useState('recent');
  const [showSortOptions, setShowSortOptions] = useState(false);
  const location = useLocation();
  const articlesPerPage = 12;

  // Opciones de ordenamiento
  const sortOptions = [
    { value: 'recent', label: 'Más recientes' },
    { value: 'oldest', label: 'Más antiguos' },
    { value: 'title_asc', label: 'Título (A-Z)' },
    { value: 'title_desc', label: 'Título (Z-A)' },
    { value: 'views_desc', label: 'Más vistos' },
    { value: 'views_asc', label: 'Menos vistos' },
  ];

  // Función para ordenar los artículos
  const getSortedArticles = (articles) => {
    const articlesToSort = [...articles];

    switch (sortOption) {
      case 'title_asc':
        return articlesToSort.sort((a, b) => a.title.localeCompare(b.title));
      case 'title_desc':
        return articlesToSort.sort((a, b) => b.title.localeCompare(a.title));
      case 'views_asc':
        return articlesToSort.sort((a, b) => a.views_count - b.views_count);
      case 'views_desc':
        return articlesToSort.sort((a, b) => b.views_count - a.views_count);
      case 'oldest':
        return articlesToSort.sort(
          (a, b) => new Date(a.published_at) - new Date(b.published_at)
        );
      case 'recent':
      default:
        return articlesToSort.sort(
          (a, b) => new Date(b.published_at) - new Date(a.published_at)
        );
    }
  };

  const sortedArticles = getSortedArticles(searchResults);

  // Aplicar paginación
  const startIndex = (currentPage - 1) * articlesPerPage;
  const endIndex = startIndex + articlesPerPage;
  const currentArticles = sortedArticles.slice(startIndex, endIndex);
  const totalPages = Math.ceil(sortedArticles.length / articlesPerPage);

  useEffect(() => {
    const searchArticles = async () => {
      try {
        setLoading(true);
        setError(null);

        const searchParams = new URLSearchParams(location.search);
        const query = searchParams.get('q') || '';

        if (!query.trim()) {
          setSearchResults([]);
          setLoading(false);
          return;
        }

        const response = await fetch(
          `${
            process.env.REACT_APP_API_BASE_URL
          }/articles/search/?q=${encodeURIComponent(query)}`
        );

        if (!response.ok) {
          throw new Error('Error en la búsqueda');
        }

        const results = await response.json();
        setSearchResults(results);
        setCurrentPage(1); // Resetear a primera página en nueva búsqueda
      } catch (error) {
        console.error('Error en la búsqueda:', error);
        setError('Error al realizar la búsqueda. Intenta nuevamente.');
        setSearchResults([]);
      } finally {
        setLoading(false);
      }
    };

    searchArticles();
  }, [location.search]);

  const searchParams = new URLSearchParams(location.search);
  const query = searchParams.get('q') || '';

  // Función para extraer texto limpio del contenido HTML
  const cleanHtml = (html) => {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '').substring(0, 200) + '...';
  };

  // Componente de paginación (igual que en CategoryPage)
  const Pagination = () => {
    if (totalPages <= 1) return null;

    return (
      <div className="sticky bottom-0 left-0 right-0 bg-white py-4 shadow-lg rounded-lg z-50 border border-gray-200 mt-8">
        <div className="flex justify-evenly space-x-2">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className={`hidden sm:flex px-4 py-2 rounded-md ${
              currentPage === 1
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-gray-200 hover:bg-gray-300'
            }`}
          >
            ANTERIOR
          </button>

          <div className="flex space-x-2">
            {/* Versión móvil */}
            <div className="sm:hidden flex space-x-2">
              {(() => {
                const mobilePages = [];
                const mobileMaxPages = 6;
                let mobileStart = Math.max(
                  1,
                  currentPage - Math.floor(mobileMaxPages / 2)
                );
                let mobileEnd = Math.min(
                  totalPages,
                  mobileStart + mobileMaxPages - 1
                );

                if (mobileEnd - mobileStart + 1 < mobileMaxPages) {
                  mobileStart = Math.max(1, mobileEnd - mobileMaxPages + 1);
                }

                for (let i = mobileStart; i <= mobileEnd; i++) {
                  mobilePages.push(
                    <button
                      key={i}
                      onClick={() => setCurrentPage(i)}
                      className={`px-3 py-2 rounded-md text-sm ${
                        currentPage === i
                          ? 'bg-[#0076ba] text-white'
                          : 'bg-gray-200 hover:bg-gray-300'
                      }`}
                    >
                      {i}
                    </button>
                  );
                }
                return mobilePages;
              })()}
            </div>

            {/* Versión desktop */}
            <div className="hidden sm:flex space-x-2">
              {(() => {
                const desktopPages = [];
                const desktopMaxPages = 3;
                let desktopStart = Math.max(
                  1,
                  currentPage - Math.floor(desktopMaxPages / 2)
                );
                let desktopEnd = Math.min(
                  totalPages,
                  desktopStart + desktopMaxPages - 1
                );

                if (desktopEnd - desktopStart + 1 < desktopMaxPages) {
                  desktopStart = Math.max(1, desktopEnd - desktopMaxPages + 1);
                }

                if (desktopStart > 1) {
                  desktopPages.push(
                    <button
                      key={1}
                      onClick={() => setCurrentPage(1)}
                      className={`px-4 py-2 rounded-md ${
                        currentPage === 1
                          ? 'bg-[#0076ba] text-white'
                          : 'bg-gray-300 hover:bg-gray-400'
                      }`}
                    >
                      1
                    </button>
                  );

                  if (desktopStart > 2) {
                    desktopPages.push(
                      <span key="ellipsis1" className="px-2 py-2">
                        ...
                      </span>
                    );
                  }
                }

                for (let i = desktopStart; i <= desktopEnd; i++) {
                  desktopPages.push(
                    <button
                      key={i}
                      onClick={() => setCurrentPage(i)}
                      className={`px-4 py-2 rounded-md ${
                        currentPage === i
                          ? 'bg-[#0076ba] text-white'
                          : 'bg-gray-200 hover:bg-gray-300'
                      }`}
                    >
                      {i}
                    </button>
                  );
                }

                if (desktopEnd < totalPages) {
                  if (desktopEnd < totalPages - 1) {
                    desktopPages.push(
                      <span key="ellipsis2" className="px-2 py-2">
                        ...
                      </span>
                    );
                  }

                  desktopPages.push(
                    <button
                      key={totalPages}
                      onClick={() => setCurrentPage(totalPages)}
                      className={`px-4 py-2 rounded-md ${
                        currentPage === totalPages
                          ? 'bg-[#0076ba] text-white'
                          : 'bg-gray-200 hover:bg-gray-300'
                      }`}
                    >
                      {totalPages}
                    </button>
                  );
                }

                return desktopPages;
              })()}
            </div>
          </div>

          <button
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
            disabled={currentPage === totalPages}
            className={`hidden sm:flex px-4 py-2 rounded-md ${
              currentPage === totalPages
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-gray-200 hover:bg-gray-300'
            }`}
          >
            SIGUIENTE
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header de resultados */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Resultados de Búsqueda
          </h1>
          {query && (
            <p className="text-lg text-gray-600">
              {loading
                ? 'Buscando...'
                : searchResults.length > 0
                ? `Se encontraron ${searchResults.length} resultados para "${query}"`
                : `No se encontraron resultados para "${query}"`}
            </p>
          )}
        </div>

        {/* Selector de ordenamiento */}
        {!loading && !error && searchResults.length > 0 && (
          <div className="flex justify-end mb-6">
            <div className="relative z-50">
              <button
                onClick={() => setShowSortOptions(!showSortOptions)}
                className="flex items-center space-x-2 bg-white border border-gray-300 rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <FunnelIcon className="h-4 w-4" />
                <span>
                  {sortOptions.find((opt) => opt.value === sortOption)?.label ||
                    'Ordenar por'}
                </span>
                <ChevronDownIcon
                  className={`h-4 w-4 transition-transform ${
                    showSortOptions ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {showSortOptions && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowSortOptions(false)}
                  />
                  <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-20">
                    {sortOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => {
                          setSortOption(option.value);
                          setShowSortOptions(false);
                          setCurrentPage(1);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition-colors ${
                          sortOption === option.value
                            ? 'bg-blue-50 text-blue-600 font-medium'
                            : 'text-gray-700'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Loading state */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
          </div>
        )}

        {/* Resultados con imágenes y nuevo diseño */}
        {!loading && !error && searchResults.length > 0 && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {currentArticles.map((article) => (
                <div
                  key={article.id}
                  className="relative bg-white rounded-lg shadow hover:shadow-lg transition"
                >
                  <a href={`/${article.slug}`}>
                    {article.featured_image_url && (
                      <img
                        src={article.featured_image_url}
                        alt={article.title}
                        className="w-full h-48 object-cover rounded-t-lg"
                      />
                    )}
                    <div className="p-4">
                      {/* Categorías */}
                      <div className="flex flex-wrap gap-2 mb-3">
                        {article.categories
                          ?.flatMap((cat) =>
                            // Aplanar recursivamente y filtrar solo las que no tienen hijos
                            (function flattenLeaves(category) {
                              if (
                                category.children &&
                                category.children.length > 0
                              ) {
                                return category.children.flatMap(flattenLeaves);
                              }
                              return [category];
                            })(cat)
                          )
                          .map((category) => (
                            <span
                              key={category.id}
                              className="inline-block px-3 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full"
                            >
                              {category.name}
                            </span>
                          ))}
                      </div>

                      <h3 className="text-lg font-bold text-[#0076ba] hover:text-[#005a96] transition-colors mb-2">
                        {article.title}
                      </h3>

                      <p className="text-sm text-gray-600 mb-3 line-clamp-3">
                        {cleanHtml(article.content)}
                      </p>

                      <div className="flex justify-between items-center text-xs text-gray-500">
                        <div className="flex items-center space-x-2">
                          <span className="block mt-3 text-gray-500">
                            {article.author?.display_name} —{' '}
                            {new Date(article.published_at).toLocaleDateString(
                              'es-MX',
                              {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric',
                              }
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  </a>
                </div>
              ))}
            </div>

            {/* Paginación */}
            <Pagination />
          </>
        )}

        {/* Sin resultados */}
        {!loading && !error && searchResults.length === 0 && query && (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="text-xl font-medium text-gray-900 mb-2">
              No se encontraron resultados
            </h3>
            <p className="text-gray-600">
              Intenta con otros términos de búsqueda o revisa la ortografía.
            </p>
          </div>
        )}

        {/* Sin término de búsqueda */}
        {!loading && !error && !query && (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📝</div>
            <h3 className="text-xl font-medium text-gray-900 mb-2">
              Ingresa un término de búsqueda
            </h3>
            <p className="text-gray-600">
              Utiliza la barra de búsqueda para encontrar artículos específicos.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchPage;
